import { Button } from "@designs-by-maryam/ui/components/button";
// biome-ignore lint/performance/noNamespaceImport: Sentry SDK requires namespace import
import * as Sentry from "@sentry/react";

/**
 * Error fallback UI shown when an error is caught by the error boundary.
 * Provides user-friendly error message and recovery options.
 */
function ErrorFallback({
	error,
	resetErrorBoundary,
}: {
	error: Error;
	resetErrorBoundary: () => void;
}) {
	return (
		<div
			aria-live="assertive"
			className="flex min-h-[50vh] flex-col items-center justify-center gap-6 px-4"
			role="alert"
		>
			<div className="text-center">
				<h2 className="mb-2 font-semibold text-2xl text-foreground">
					Something went wrong
				</h2>
				<p className="max-w-md text-muted-foreground">
					We've been notified and are working to fix the issue. Please try
					refreshing the page.
				</p>
			</div>

			{/* Only show error details in development */}
			{import.meta.env.DEV && (
				<details className="max-w-lg rounded-md border bg-muted/50 p-4 text-left">
					<summary className="cursor-pointer font-medium text-sm">
						Error Details
					</summary>
					<pre className="mt-2 overflow-auto whitespace-pre-wrap text-destructive text-xs">
						{error.message}
						{error.stack && `\n\n${error.stack}`}
					</pre>
				</details>
			)}

			<div className="flex gap-4">
				<Button onClick={() => window.location.reload()} variant="outline">
					Refresh Page
				</Button>
				<Button onClick={resetErrorBoundary}>Try Again</Button>
			</div>
		</div>
	);
}

/**
 * Sentry-integrated error boundary wrapper.
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them to Sentry, and displays a fallback UI.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export function ErrorBoundary({ children }: { children: React.ReactNode }) {
	return (
		<Sentry.ErrorBoundary
			fallback={({ error, resetError }) => (
				<ErrorFallback
					error={error instanceof Error ? error : new Error(String(error))}
					resetErrorBoundary={resetError}
				/>
			)}
			showDialog={false} // Don't show Sentry's dialog, use our custom UI
		>
			{children}
		</Sentry.ErrorBoundary>
	);
}

/**
 * Route-level error boundary for TanStack Router.
 * Shows a more contextual error message for route errors.
 */
export function RouteErrorBoundary({
	children,
}: {
	children: React.ReactNode;
}) {
	return (
		<Sentry.ErrorBoundary
			fallback={({ error, resetError }) => {
				const errorObj =
					error instanceof Error ? error : new Error(String(error));
				return (
					<div
						aria-live="assertive"
						className="flex min-h-[50vh] flex-col items-center justify-center gap-6 px-4"
						role="alert"
					>
						<div className="text-center">
							<h2 className="mb-2 font-semibold text-2xl text-foreground">
								Page Error
							</h2>
							<p className="max-w-md text-muted-foreground">
								This page encountered an error. Try navigating to a different
								page or refreshing.
							</p>
						</div>

						{import.meta.env.DEV && (
							<details className="max-w-lg rounded-md border bg-muted/50 p-4 text-left">
								<summary className="cursor-pointer font-medium text-sm">
									Error Details
								</summary>
								<pre className="mt-2 overflow-auto whitespace-pre-wrap text-destructive text-xs">
									{errorObj.message}
								</pre>
							</details>
						)}

						<div className="flex gap-4">
							<Button onClick={() => window.history.back()} variant="outline">
								Go Back
							</Button>
							<Button onClick={resetError}>Try Again</Button>
						</div>
					</div>
				);
			}}
		>
			{children}
		</Sentry.ErrorBoundary>
	);
}
